import { useCallback, useRef, useState } from 'react';

import { smallUrlOf, useAssetUrls } from './useAssetUrls';

/**
 * The source images an asset was cut from, as pictures rather than codes.
 *
 * `SI-000042` and `SI-000043` are indistinguishable without opening them, so a
 * picker offering only codes is a picker people get wrong and find out about
 * later. This is the thumbnail that answers "which hero is this?" in place.
 *
 * Takes an array, always. An asset can be cut from several shoots —
 * `source_image_ids` is a join table, not a single FK — so a stack of one and a
 * stack of five have to be the same component. Callers pass the whole list and
 * let the stack decide how much of it fits.
 *
 * It signs its own URLs rather than reading the page's map. A staged banner's
 * source image is usually published, and therefore not on the page the grid
 * signed — the case `AssetLibraryPage` patches with its `fallbackUrls` merge.
 * Asking for its own keeps the component usable anywhere without the caller
 * having to know which query a URL came from. Two callers showing the same
 * source image share one request: the hook keys on the sorted id array.
 */

/** How many thumbnails the stack shows before it gives up and counts. */
const STACK_LIMIT = 4;

/** Same budget, and same reasoning, as the grid card: one re-sign per failure. */
const MAX_URL_RETRIES = 1;

const SIZES = {
  sm: 'h-7 w-7',
  md: 'h-9 w-9',
  lg: 'h-14 w-full',
} as const;

/**
 * One tile, and the recovery behaviour every caller of it needs.
 *
 * Shared by the stack below and by the picker grid, because "signed URL that
 * may have died since it was signed" is the same problem in both and it is not
 * a problem worth solving twice.
 */
export function useSourceImageTiles() {
  // The URLs that failed, not a per-id boolean: a freshly signed URL is a
  // different string, so a tile un-breaks itself when the re-sign lands without
  // needing an effect to clear the flag. Keyed by URL rather than by id for the
  // same reason — the id is unchanged across a re-sign, the URL is not.
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(new Set());
  // Budget per asset, not one shared across the group. The picker renders a
  // grid of these, and a single counter meant a permanently missing image had
  // its budget handed back every time some *other* tile loaded — an endless
  // re-sign on every cycle, which is the opposite of what the limit is for.
  //
  // Per asset rather than per URL, even though the failures are tracked by URL:
  // a re-sign produces a different URL string, so a per-URL counter would start
  // each replacement at zero and never stop retrying at all.
  const retries = useRef(new Map<string, number>());

  const handleError = useCallback((id: string, url: string, refresh: () => void) => {
    setFailedUrls((previous) => new Set(previous).add(url));
    const spent = retries.current.get(id) ?? 0;
    if (spent >= MAX_URL_RETRIES) return;
    retries.current.set(id, spent + 1);
    refresh();
  }, []);

  // A successful load ends that tile's run of failures, so the next expiry —
  // hours later, on a long-lived tab — gets its own retry rather than falling
  // straight through to the code. Only that tile's: see above.
  const handleLoad = useCallback((id: string) => {
    retries.current.delete(id);
  }, []);

  return { failedUrls, handleError, handleLoad };
}

interface SourceImageTileProps {
  url: string | undefined;
  code: string | undefined;
  filename?: string;
  size: keyof typeof SIZES;
  isBroken: boolean;
  onError: () => void;
  onLoad: () => void;
  className?: string;
}

export function SourceImageTile({
  url,
  code,
  filename,
  size,
  isBroken,
  onError,
  onLoad,
  className = '',
}: SourceImageTileProps) {
  const label = [code, filename].filter(Boolean).join(' · ') || undefined;

  return (
    <span
      title={label}
      className={`flex ${SIZES[size]} items-center justify-center overflow-hidden bg-slate-100 ${className}`}
    >
      {url && !isBroken ? (
        <img
          src={url}
          // Named rather than decorative: this image is the only thing on
          // screen distinguishing one source image from another, so an empty
          // alt would hide the whole point of it from a screen reader and
          // leave the code as the only way through.
          alt={label ? `Source image ${label}` : 'Source image'}
          loading="lazy"
          onError={onError}
          onLoad={onLoad}
          className="h-full w-full object-cover"
        />
      ) : (
        // Deliberately the code and not an `ImageOff` icon: at this size the
        // icon is indistinguishable from the browser's own broken-image glyph,
        // which is the thing being avoided. The code is also the more useful of
        // the two — it is what the user was reading before the picture was
        // offered.
        <span className="px-0.5 text-center text-[8px] font-medium leading-tight text-slate-400">
          {code ?? ''}
        </span>
      )}
    </span>
  );
}

interface SourceImageThumbsProps {
  /** Always an array. See the note above on why this is never a bare id. */
  sourceImageIds: string[];
  size?: keyof typeof SIZES;
  /** Source image code by id, for the tooltip and the degraded state. */
  codes?: Map<string, string>;
  /** Filename by id, for the tooltip's second half. */
  filenames?: Map<string, string>;
  /**
   * Defer signing until the thumbnails are actually wanted. The grid renders
   * five hundred cards; signing a source image for each one on mount would be
   * five hundred URLs to show none of them.
   */
  enabled?: boolean;
}

export function SourceImageThumbs({
  sourceImageIds,
  size = 'md',
  codes,
  filenames,
  enabled = true,
}: SourceImageThumbsProps) {
  // Deduped: two derivatives of one hero are one picture, and a stack showing
  // it twice reads as a mistake.
  const ids = Array.from(new Set(sourceImageIds));
  const urls = useAssetUrls(enabled ? ids : []);
  const { failedUrls, handleError, handleLoad } = useSourceImageTiles();

  // Nothing to show is nothing, not an empty frame. An asset with no source
  // image is the normal state of a hero, and a placeholder box there reads as
  // a thumbnail that failed.
  if (ids.length === 0) return null;

  const stack = ids.slice(0, STACK_LIMIT);
  const overflow = ids.length - stack.length;

  return (
    <div className="flex items-center -space-x-2.5">
      {stack.map((id) => {
        const url = smallUrlOf(urls, id);
        return (
          <SourceImageTile
            key={id}
            url={url}
            code={codes?.get(id)}
            filename={filenames?.get(id)}
            size={size}
            isBroken={url !== undefined && failedUrls.has(url)}
            onError={() => url && handleError(id, url, urls.refresh)}
            onLoad={() => handleLoad(id)}
            className="rounded-lg border-2 border-white ring-1 ring-slate-200"
          />
        );
      })}
      {overflow > 0 && (
        <span
          className={`flex ${SIZES[size]} items-center justify-center rounded-lg border-2 border-white bg-slate-800 text-[11px] font-semibold text-white ring-1 ring-slate-200`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
